import { useEffect, useState } from "react";
import { ChevronDown, ChevronUp, Flag } from "lucide-react";
import api from "@/lib/api";

/**
 * Read-only RHYTHM direct-feedback history for a given employee.
 * Shown on the COMPASS entry form and plan detail so managers and PO+
 * can see the trail of documented direct feedback before / during a plan.
 * Non-blocking — if the caller lacks access we quietly show an empty state.
 */
export default function DirectFeedbackHistoryPanel({ employeeId, defaultOpen = true }) {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(defaultOpen);
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    if (!employeeId) { setRows([]); return; }
    let cancelled = false;
    (async () => {
      setErrored(false);
      try {
        const { data } = await api.get(`/rhythm/compass/direct-feedback-history/${employeeId}`);
        if (!cancelled) setRows(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) { setErrored(true); setRows([]); }
      }
    })();
    return () => { cancelled = true; };
  }, [employeeId]);

  if (!employeeId) return null;
  return (
    <section
      className="patria-card p-5 border-l-4 border-terracotta-500"
      data-testid="compass-rhythm-history"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 text-left"
      >
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-full bg-terracotta-500/15 text-terracotta-600 flex items-center justify-center shrink-0">
            <Flag className="h-4 w-4" strokeWidth={1.5} />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-widest text-terracotta-500 font-semibold">
              RHYTHM · Direct Feedback History
            </p>
            <p className="text-sm text-charcoal-soft">
              Flagged direct-feedback meetings from RHYTHM 1:1s — evidence that a gap was
              named and discussed before a formal COMPASS entry.
            </p>
          </div>
        </div>
        <div className="text-forest-700">
          {open ? <ChevronUp className="h-5 w-5" strokeWidth={1.5} /> : <ChevronDown className="h-5 w-5" strokeWidth={1.5} />}
        </div>
      </button>

      {open && (
        <div className="mt-4">
          {rows === null && <p className="italic text-charcoal-muted text-sm">Loading history…</p>}
          {errored && (
            <p className="text-xs italic text-charcoal-muted">
              History not available to you (requires PO+ or being the pairing supervisor).
            </p>
          )}
          {rows && rows.length === 0 && !errored && (
            <p className="text-xs italic text-charcoal-muted" data-testid="compass-rhythm-history-empty">
              No direct-feedback flags recorded in RHYTHM yet.
            </p>
          )}
          {rows && rows.length > 0 && (
            <ul className="space-y-2" data-testid="compass-rhythm-history-list">
              {rows.map((r) => (
                <li key={r.meeting_id} className="p-3 rounded-xl bg-cream-100">
                  <p className="text-sm text-forest-900">{r.topic_note || "(no topic note)"}</p>
                  <p className="text-[10px] uppercase tracking-widest text-charcoal-muted mt-1">
                    {r.date} · Tags: {(r.tags || []).join(", ") || "—"}
                    {r.acknowledged && " · Acknowledged"}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}
